// src/components/common/Header.js
import React, { useState } from 'react';
import { Menu, X, ShoppingCart, Search, User, LogOut, Home, Store, Info, Star, Phone, Award } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

function Header({ isMobile, currentPage, setCurrentPage, cartItemCount, user, onLogout }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const navItems = [
    { id: 'home', label: 'Home', icon: <Home className="w-4 h-4" /> },
    { id: 'shop', label: 'Shop', icon: <Store className="w-4 h-4" /> },
    { id: 'about', label: 'About', icon: <Info className="w-4 h-4" /> },
    { id: 'products', label: 'Products', icon: <Award className="w-4 h-4" /> },
    { id: 'testimonials', label: 'Reviews', icon: <Star className="w-4 h-4" /> },
    { id: 'contact', label: 'Contact', icon: <Phone className="w-4 h-4" /> },
  ];

  return (
    <motion.header 
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ type: 'spring', stiffness: 100 }}
      className="sticky top-0 z-50 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white shadow-2xl backdrop-blur-md bg-opacity-95 border-b border-amber-500/20"
    >
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <motion.div 
            whileHover={{ scale: 1.05 }}
            className="flex items-center space-x-3 cursor-pointer group"
            onClick={() => setCurrentPage('home')}
          >
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full blur opacity-30 group-hover:opacity-70 transition duration-300"></div>
              <div className="relative w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center">
                <span className="text-xl font-bold">🥩</span>
              </div>
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-amber-300 via-orange-400 to-yellow-300 bg-clip-text text-transparent">
                Sir Henry Butchery
              </h1>
              <p className="text-xs text-amber-200 opacity-80">Premium Quality Meats</p>
            </div>
          </motion.div>

          {/* Desktop Navigation */}
          {!isMobile && (
            <nav className="flex-1 mx-8">
              <ul className="flex items-center justify-center space-x-1">
                {navItems.map((item) => (
                  <li key={item.id}>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        setCurrentPage(item.id === 'about' || item.id === 'products' || item.id === 'testimonials' || item.id === 'contact' ? 'home' : item.id);
                        if (item.id !== 'home' && item.id !== 'shop') {
                          setTimeout(() => {
                            document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' });
                          }, 100);
                        }
                      }}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-full transition-all duration-300 ${
                        currentPage === item.id 
                          ? 'bg-gradient-to-r from-amber-500 to-orange-600 shadow-lg shadow-amber-500/30'
                          : 'hover:bg-white/10 backdrop-blur-sm'
                      }`}
                    >
                      {item.icon}
                      <span className="font-medium">{item.label}</span>
                    </motion.button>
                  </li>
                ))}
              </ul>
            </nav>
          )}

          {/* Right side buttons */}
          <div className="flex items-center space-x-3">
            {/* Search */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => setSearchOpen(!searchOpen)}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <Search className="w-5 h-5" />
            </motion.button>

            {/* User/Auth */}
            {user ? (
              <div className="flex items-center space-x-3">
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="hidden md:flex items-center space-x-2 bg-white/5 px-3 py-1 rounded-full"
                >
                  <User className="w-4 h-4" />
                  <span className="text-sm text-amber-300">Welcome, {user.name?.split(' ')[0] || user.email}</span>
                </motion.div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setCurrentPage('dashboard')}
                  className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-600 rounded-full text-sm font-semibold shadow-lg shadow-amber-500/30 hover:shadow-xl transition-all"
                >
                  Dashboard
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onLogout}
                  className="px-4 py-2 border border-red-400 text-red-400 hover:bg-red-400/10 rounded-full text-sm transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                </motion.button>
              </div>
            ) : (
              <div className="flex space-x-2">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setCurrentPage('login')}
                  className="px-4 py-2 border border-amber-500 text-amber-500 hover:bg-amber-500/10 rounded-full text-sm transition-colors"
                >
                  Login
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setCurrentPage('signup')}
                  className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-600 rounded-full text-sm font-semibold shadow-lg shadow-amber-500/30 hover:shadow-xl transition-all"
                >
                  Sign Up
                </motion.button>
              </div>
            )}

            {/* Cart */}
            <motion.div
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="relative cursor-pointer"
              onClick={() => setCurrentPage('checkout')}
            >
              <div className="p-2 hover:bg-white/10 rounded-full transition-colors">
                <ShoppingCart className="w-6 h-6" />
                {cartItemCount > 0 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-1 -right-1 bg-gradient-to-r from-red-500 to-pink-600 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold shadow-lg"
                  >
                    {cartItemCount}
                  </motion.span>
                )}
              </div>
            </motion.div>

            {/* Mobile menu button */}
            {isMobile && (
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={() => setMenuOpen(!menuOpen)}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </motion.button>
            )}
          </div>
        </div>

        {/* Search bar */}
        <AnimatePresence>
          {searchOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4"
            >
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search premium cuts, specials, and more..."
                  className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-amber-500/30 rounded-full pl-12 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-transparent"
                />
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-amber-300" />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMobile && menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="absolute top-full left-0 right-0 bg-gradient-to-b from-gray-900 to-gray-800 border-t border-amber-500/20 backdrop-blur-xl"
          >
            <div className="container mx-auto px-4 py-6">
              <ul className="space-y-2">
                {navItems.map((item, index) => (
                  <motion.li
                    key={item.id}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <button
                      onClick={() => {
                        setCurrentPage(item.id === 'about' || item.id === 'products' || item.id === 'testimonials' || item.id === 'contact' ? 'home' : item.id);
                        setMenuOpen(false);
                        if (item.id !== 'home' && item.id !== 'shop') {
                          setTimeout(() => {
                            document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' });
                          }, 100);
                        }
                      }}
                      className={`flex items-center space-x-3 w-full px-4 py-3 rounded-xl transition-all ${
                        currentPage === item.id
                          ? 'bg-gradient-to-r from-amber-500 to-orange-600 shadow-lg'
                          : 'hover:bg-white/10'
                      }`}
                    >
                      {item.icon}
                      <span className="font-medium">{item.label}</span>
                    </button>
                  </motion.li>
                ))}
              </ul>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}

export default Header;